import { useState, type ReactElement } from 'react';
import { Button, Select, Tag, Tooltip, Alert } from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { EntityRelatedContext } from '../../../components/entity-detail/related-context.js';
import {
  formatDateTime,
  formatMeetingRange,
  browserTimeZone,
} from '../../../shared/format/entity-detail.js';
import { formatPersonName } from '../../../shared/format/index.js';
import {
  useMeetings,
  useDeleteMeetingMutation,
  useUpdateMeetingMutation,
} from '../../meetings/hooks/useMeetings.js';
import { MeetingFormModal } from '../../meetings/components/MeetingFormModal.js';
import { meetingIcsUrl, type MeetingItem } from '../../meetings/api/meetings.client.js';

export function ContactMeetingsTab({ contactId }: { contactId: string }): ReactElement {
  return <EntityMeetingsTab contactId={contactId} />;
}

export function EntityMeetingsTab({
  contactId,
  dealId,
  companyId,
  taskId,
}: EntityRelatedContext): ReactElement {
  const { t, locale: i18nLocale } = useTranslation();
  const query = useMeetings(
    taskId
      ? { taskId }
      : dealId
        ? { dealId }
        : companyId
          ? { companyId }
          : { contactId: contactId! },
  );
  const remove = useDeleteMeetingMutation();
  const update = useUpdateMeetingMutation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MeetingItem | null>(null);
  const [sort, setSort] = useState('date');
  const [direction, setDirection] = useState<'ascend' | 'descend'>('ascend');
  return (
    <>
      {update.error || remove.error ? (
        <Alert type="error" title={t('crm.entity_detail.save_error')} />
      ) : null}
      <EntityRelatedList
        loading={query.isLoading}
        error={query.error}
        emptyText={t('crm.entity_detail.empty_meetings')}
        filter={
          query.data?.length ? (
            <div className="contact-deals-filter company-detail-related-sort">
              <Select
                size="small"
                aria-label={t('crm.entity_detail.sort')}
                value={sort}
                onChange={setSort}
                style={{ minWidth: 160 }}
                options={[
                  { value: 'original', label: t('crm.features.company_detail.sort_original') },
                  { value: 'date', label: t('crm.entity_detail.sort_due') },
                  { value: 'title', label: t('crm.entity_detail.sort_title') },
                ]}
              />
              <Select
                size="small"
                aria-label={t('crm.features.company_detail.sort_direction')}
                value={direction}
                onChange={setDirection}
                disabled={sort === 'original'}
                style={{ minWidth: 160 }}
                options={['ascend', 'descend'].map((value) => ({
                  value,
                  label: t(`crm.features.company_detail.sort_${value}`),
                }))}
              />
            </div>
          ) : null
        }
        action={
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            {t('crm.entity_detail.add_meeting')}
          </Button>
        }
        rows={[...(query.data ?? [])]
          .sort((a, b) => {
            if (sort === 'original') return 0;
            const order =
              sort === 'title'
                ? a.title.localeCompare(b.title, i18nLocale)
                : Date.parse(a.startsAt) - Date.parse(b.startsAt);
            return direction === 'descend' ? -order : order;
          })
          .map((meeting) => ({
            id: meeting.id,
            title: meeting.title,
            href: `/meetings/${meeting.id}`,
            status: (
              <EntityStatusChip
                tone={
                  meeting.status === 'completed'
                    ? 'success'
                    : meeting.status === 'cancelled'
                      ? 'default'
                      : Date.parse(meeting.endsAt) < Date.now()
                        ? 'warning'
                        : 'processing'
                }
              >
                {t(
                  `crm.entity_detail.meeting_${meeting.status === 'scheduled' && Date.parse(meeting.endsAt) < Date.now() ? 'unmarked' : meeting.status}`,
                )}
              </EntityStatusChip>
            ),
            meta: meeting.participants
              .map((p) => {
                const name =
                  p.member?.user.name ??
                  p.member?.user.email ??
                  (p.contact
                    ? formatPersonName(p.contact.firstName, p.contact.lastName)
                    : (p.guestName ?? p.guestEmail));
                return name ? <Tag key={p.id}>{name}</Tag> : null;
              })
              .filter(Boolean),
            value: (
              <Tooltip
                title={
                  meeting.timezone !== browserTimeZone()
                    ? formatDateTime(meeting.startsAt, meeting.timezone)
                    : undefined
                }
              >
                <time dateTime={meeting.startsAt}>
                  {formatMeetingRange(meeting.startsAt, meeting.endsAt)}
                </time>
              </Tooltip>
            ),
            visibleActions: (
              <Tooltip title={t('crm.entity_detail.download_ics')}>
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  aria-label={t('crm.entity_detail.download_ics')}
                  onClick={() => {
                    window.open(meetingIcsUrl(meeting.id), '_blank', 'noopener,noreferrer');
                  }}
                />
              </Tooltip>
            ),
            actions: [
              {
                key: 'edit',
                label: t('crm.entity_detail.edit'),
                onClick: () => {
                  setEditing(meeting);
                  setOpen(true);
                },
              },
              {
                key: 'cancel',
                label: t('crm.entity_detail.cancel_meeting'),
                disabled: meeting.status === 'cancelled' || update.isPending,
                confirm: { title: t('crm.entity_detail.cancel_meeting_confirm') },
                onClick: () =>
                  update.mutateAsync({ id: meeting.id, input: { status: 'cancelled' } }),
              },
              {
                key: 'delete',
                label: t('crm.entity_detail.delete'),
                danger: true,
                disabled: remove.isPending,
                confirm: { title: t('crm.entity_detail.delete_confirm') },
                onClick: () => remove.mutateAsync(meeting.id),
              },
            ],
          }))}
      />
      {open ? (
        <MeetingFormModal
          open
          meeting={editing}
          {...(taskId
            ? { lockedTaskId: taskId }
            : dealId
              ? { lockedDealId: dealId }
              : companyId
                ? { lockedCompanyId: companyId }
                : { lockedContactId: contactId! })}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
